import { Button, Loading, Message, Title } from "@components";

import styles from "styles/pages/reports/reports.module.scss";
import ContemplationXUses from "./ContemplationXUses";
import UsageBehavior from "./UsageBehavior";
import UsesAndExpiration from "./UsesAndExpiration";
import CouponsInPeriod from "./CouponsInPeriod";
import UsageRecord from "pages/Reports/Coupon/UsageRecord";
import { Currency } from "shared/utils/Currency";
import { If } from "shared/components/control-statements";
import ReportFilters from "pages/Reports/ReportFilters";
import { useEffect, useRef, useState } from "react";
import Filters from "pages/Reports/Filters";
import { ReportsAPI } from "providers/ReportsAPI";
import { Numbers } from "shared/utils/Numbers";
import { OlgaEnvironmentsAPI } from "providers/OlgaEnvironmentsAPI";
import DistribuitionUses from "./DistribuitionUses";
import dayjs from "dayjs";

const numbers = (data = {}, isBeforeFebruary) => {
  return [
    {
      title: "Faturamento bruto",
      value: Currency.formatCurrency(data.total),
      tooltip:
        "Soma do valor total bruto de todos os pedidos utilizando cupons.",
    },
    {
      title: "Descontos totais",
      value: isBeforeFebruary ? "---" : Currency.formatCurrency(data.discounts),
      tooltip:
        "Soma do valor total de descontos aplicados nos pedidos que utilizaram cupons.",
    },
    {
      title: "Faturamento líquido",
      value: isBeforeFebruary
        ? "---"
        : Currency.formatCurrency(data.total_with_discounts),
      tooltip:
        "Soma do valor total líquido de todos os pedidos utilizando cupons.",
    },
  ];
};

const numbers2 = (data = {}) => {
  return [
    {
      title: "Quantidade de usos",
      value: Numbers.formatFloat(data.uses),
    },
    {
      title: "Taxa de conversão",
      value: Numbers.formatPercent(data.conversion_rate),
      tooltip: "",
    },
    {
      title: "Taxa de expiração",
      value: Numbers.formatPercent(data.expiration_rate),
      tooltip: "",
    },
  ];
};

const numbers3 = (data = {}, isBeforeFebruary) => {
  return [
    {
      title: "Ticket médio bruto",
      value: Currency.formatCurrency(data.average_ticket),
      tooltip:
        "Soma do valor total bruto dos pedidos que utilizaram este cupom, divididos pela quantidade de pedidos que utilizaram cupons.",
    },
    {
      title: "Ticket médio líquido",
      value: isBeforeFebruary
        ? "---"
        : Currency.formatCurrency(data.average_ticket_with_discounts),
      tooltip:
        "Soma do valor total líquido dos pedidos que utilizaram este cupom, divididos pela quantidade de pedidos que utilizaram cupons.",
    },
    {
      title: "Tempo médio para uso",
      value: `${Numbers.formatFloat(data.time_to_use)} dias`,
      tooltip: "Média do tempo entre as adições e os usos deste cupom.",
    },
    {
      title: "Roi dos cupons",
      value: isBeforeFebruary ? "---" : data.roi,
      tooltip:
        "Retorno sobre o investimento do cupom. FÓRMULA: {{ ((Faturamento gerado - Descontos totais) / Descontos totais) * 100 }}.",
    },
  ];
};

const BigNumber = ({ data }) => {
  return (
    <div className={styles.bigNumber}>
      <div className="d-flex flex-column">
        <div className="d-flex align-center">
          <p className={styles.number}>{data.value}</p>
        </div>
        <div className={styles.title}>
          <p>{data.title}</p>
          <If cond={data.tooltip}>
            <span className={styles.info} title={data.tooltip}>
              <i className="fal fa-info-circle" />
            </span>
          </If>
        </div>
      </div>
    </div>
  );
};

export default function Index() {
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [dateFilters, setDateFilters] = useState({});
  const [couponUses, setCouponUses] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  // const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = couponUses.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(couponUses.length / itemsPerPage);

  const filtersRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    behavior: [],
    distribution: {},
    expiration_uses: [],
    gain_uses: {},
    period: [],
    statistics: {},
  });
  const [label_dict, setLabelDict] = useState(new Map());

  const fetchData = async () => {
    const provider = new ReportsAPI();
    const response = await provider.coupons({
      ...filters,
      ...(filters.period === "FROM_THE_BEGINNING" && {
        start_date: null,
        end_date: null,
      }),
      ...(!filters.period && {
        start_date: dayjs().startOf("month").format("YYYY-MM-DD"),
        end_date: dayjs().endOf("month").format("YYYY-MM-DD"),
      }),
    });

    setData(response);
  };

  const fetchCouponUses = async (page = 1, page_size = 10) => {
    const provider = new ReportsAPI();
    const response = await provider.couponsType(
      "uses",
      { page, page_size },
      {
        ...filters,
        ...(filters.period === "FROM_THE_BEGINNING" && {
          start_date: null,
          end_date: null,
        }),
        ...(!filters.period && {
          start_date: dayjs().startOf("month").format("YYYY-MM-DD"),
          end_date: dayjs().endOf("month").format("YYYY-MM-DD"),
        }),
      }
    );

    setCouponUses(response.data || []);
  };

  useEffect(() => {
    const fetchDataAsync = async () => {
      setLoading(true);
      await Promise.all([fetchData(), fetchCouponUses(1, 3000), fetchUnits()]);
      setLoading(false);
    };

    fetchDataAsync();
  }, [filters]);

  const fetchUnits = async () => {
    const response_units = await OlgaEnvironmentsAPI.getUnits();
    const response_brands = await OlgaEnvironmentsAPI.getBrands();

    const units = response_units.getData([]);
    const brands = response_brands.getData([]);

    setLabelDict(
      new Map([
        ...units.map((unit) => [`units_${unit.id}`, unit.name]),
        ...brands.map((brand) => [`brands_${brand.id}`, brand.name]),
      ])
    );
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      // fetchCouponUses(page);
      setCurrentPage(page);
    }
  };

  const september2025 = dayjs("2025-09-01T00:00:00.000Z");
  const february2025 = dayjs("2025-02-01T00:00:00.000Z");
  const currentBeforeSeptember = dayjs(filters?.start_date || "").isBefore(
    september2025,
    "month"
  );
  const currentBeforeFebruary = dayjs(filters?.start_date || "").isBefore(
    february2025
  );

  const isBeforeSeptember = currentBeforeSeptember;
  const isBeforeFebruary =
    currentBeforeFebruary || filters?.period === "FROM_THE_BEGINNING";

  const loadCoupons = async () => {
    const provider = new ReportsAPI();
    const response = await provider.getAllCoupons();

    setCoupons(response);
  };

  useEffect(() => {
    loadCoupons();
  }, []);

  const specificDay = filters?.period === "SPECIFIC_DAY";

  return (
    <>
      <Title
        title="Cupons"
        backTo={{ url: "/relatorios/marketing", name: "Marketing" }}
      >
        <Filters
          onRemoveFilter={(filter) => {
            filtersRef.current?.removeFilter(filter);
          }}
          filters={filters}
          units={label_dict}
        />
        <Button onClick={() => setOpenFilters(true)}>Filtrar</Button>
      </Title>

      <If cond={isBeforeFebruary}>
        <Message type="warning" className="mb-15">
          Devido ao período filtrado, alguns dados não serão exibidos.
        </Message>
      </If>

      <div className={styles.bigNumbersIdentification}>
        {numbers(data.statistics || {}, isBeforeFebruary).map((item) => (
          <BigNumber key={item.title} data={item} />
        ))}
      </div>
      <div className={styles.bigNumbersIdentification}>
        {numbers2(data.statistics || {}, isBeforeFebruary).map((item) => (
          <BigNumber key={item.title} data={item} />
        ))}
      </div>
      <div className={styles.bigNumbersIdentification4}>
        {numbers3(data.statistics || {}, isBeforeFebruary).map((item) => (
          <BigNumber key={item.title} data={item} />
        ))}
      </div>

      <If cond={loading}>
        <Loading fullContent />
      </If>

      <If cond={!specificDay}>
        <CouponsInPeriod data={data.period} />
      </If>
      <DistribuitionUses
        data={data.distribution}
        isBeforeSeptember={isBeforeSeptember}
        isBeforeFebruary={isBeforeFebruary}
        filters={filters}
      />
      <ContemplationXUses data={data.gain_uses} />
      <UsageBehavior data={data.behavior} />
      {/* <UsesAndExpiration data={data?.expiration_uses} /> */}
      <UsageRecord
        data={currentItems}
        handlePageChange={handlePageChange}
        currentPage={currentPage}
        totalPages={totalPages}
      />

      <ReportFilters
        ref={filtersRef}
        open={openFilters}
        onClose={() => setOpenFilters(false)}
        dateFilters={dateFilters}
        onUpdateFilters={(filters) => setFilters(filters)}
        onUpdateDateFilters={(filters) => {
          setDateFilters(filters);
        }}
        showCoupons={true}
        coupons={coupons}
      />
    </>
  );
}
